import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { TabItem, Tab, Separator } from '@/config/navigation';
import { LanguageNativeName } from './NavIcons';
import { EditableContent } from '../EditableContent';

const spanVariants = {
    initial: { width: 0, opacity: 0 },
    animate: {
        width: "auto", opacity: 1,
        transition: { delay: 0.05, duration: 0.2, ease: "easeOut" as const },
    },
    exit: {
        width: 0, opacity: 0,
        transition: { duration: 0.1, ease: "easeIn" as const },
    },
};

export function DesktopNav({ tabs, isRTL = false }: { tabs: TabItem[]; isRTL?: boolean }) {
    const [selected, setSelected] = useState<number | null>(null);
    const [mounted, setMounted] = useState(false);
    const pathname = usePathname();
    const { language } = useLanguage();

    useEffect(() => {
        setMounted(true);
    }, []);

    // Initialize selected tab based on current route
    useEffect(() => {
        if (!pathname) return;

        // Normalize pathname (remove trailing slash and hash if any)
        let path = pathname;
        const hashIdx = path.indexOf('#');
        if (hashIdx !== -1) path = path.slice(0, hashIdx);
        if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

        // Special-case: ensure Contact highlights for any /contact path
        const contactIdx = tabs.findIndex((t) => (t as Tab).url === '/contact');
        if (contactIdx !== -1 && (path === '/contact' || path.startsWith('/contact/'))) {
            setSelected(contactIdx);
            return;
        }

        // 1) Prefer exact match with direct tab URLs
        let idx = tabs.findIndex((t) => {
            const tab = t as Tab;
            if ((t as Separator).type === 'separator') return false;
            return !!tab.url && path === tab.url;
        });

        // 2) If no exact match, check dropdown items
        if (idx < 0) {
            idx = tabs.findIndex((t) => {
                const tab = t as Tab;
                if ((t as Separator).type === 'separator') return false;
                return !!(tab.items && tab.items.some((it) => path.startsWith(it.url)));
            });
        }

        // 3) Fallbacks (optional): if still not found, clear selection
        setSelected(idx >= 0 ? idx : null);
    }, [pathname, tabs]);

    // For RTL, render tabs in reverse so "Home" remains at the far right
    const tabsToRender = isRTL ? [...tabs].reverse() : tabs;
    const currentSelectedTab = typeof selected === 'number' ? (tabs as TabItem[])[selected] as Tab : null;

    return (
        <div className="flex items-center gap-1 rounded-xl bg-white dark:bg-black p-1 shadow-md border border-gray-200 dark:border-gray-700" dir={isRTL ? 'rtl' : 'ltr'}>
            <NavigationMenu viewport={false}>
                <NavigationMenuList className={`flex items-center gap-1`}>
                    {tabsToRender.map((tab, idx) => {
                        if ((tab as Separator).type === "separator") {
                            return <div key={idx} className="h-7 w-px bg-slate-200 dark:bg-slate-700" />;
                        }

                        const Icon = (tab as Tab).icon;
                        const tabData = tab as Tab;
                        const isSelected = currentSelectedTab === tabData;
                        const isLanguageTab = tabData.key === 'nav.language';

                        // For dropdown items
                        if (tabData.dropdown && tabData.items) {
                            return (
                                <NavigationMenuItem key={idx}>
                                    <NavigationMenuTrigger
                                        className={`relative z-10 flex items-center rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none border-0 bg-transparent hover:bg-transparent data-[state=open]:bg-transparent ${isSelected
                                            ? "text-[#ED2245]"
                                            : "text-slate-700 dark:text-white hover:text-[#ED2245] dark:hover:text-[#ED2245]"
                                            }`}
                                        onClick={() => {
                                            setSelected(tabs.indexOf(tabData));
                                            if ('onClick' in tabData && tabData.onClick) tabData.onClick();
                                        }}
                                        aria-label={tabData.title}
                                    >
                                        {isSelected && (
                                            <motion.div
                                                layoutId="pill"
                                                className="absolute inset-0 z-0 rounded-xl bg-[#ED2245]/10 border border-[#ED2245]/30 shadow-sm red-glow-pulse"
                                                transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                            />
                                        )}
                                        <span className="relative z-10 flex items-center gap-2">
                                            <Icon className="h-5 w-5 flex-shrink-0" />
                                            <AnimatePresence initial={false}>
                                                {isSelected && (
                                                    <motion.span variants={spanVariants} initial="initial" animate="animate" exit="exit" className="overflow-hidden whitespace-nowrap">
                                                        {tabData.key ? (
                                                            <EditableContent id={tabData.key} showEditIcon={true} />
                                                        ) : (
                                                            tabData.title
                                                        )}
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </span>
                                    </NavigationMenuTrigger>
                                    <NavigationMenuContent
                                        className={`${(tabData.title.includes('Solutions') || tabData.title.includes('حلول') || tabData.key === 'nav.solutions')
                                            ? (isRTL
                                                ? 'md:left-auto md:right-1/2 md:translate-x-1/2'
                                                : 'md:left-1/2 md:-translate-x-1/2')
                                            : ''}`}
                                    >
                                        {/* Check if this is the Solutions dropdown for special 3-column layout */}
                                        {(tabData.title.includes('Solutions') || tabData.title.includes('حلول') || tabData.key === 'nav.solutions') ? (
                                            <div className="w-[800px] p-6 bg-white dark:bg-black shadow-lg border border-slate-200 dark:border-slate-700 rounded-lg">
                                                <div className="grid grid-cols-3 gap-8">
                                                    {/* Merchant Solutions Column */}
                                                    <div>
                                                        <h3 className="font-semibold text-lg mb-4 text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                                                            <EditableContent id="nav.merchant_solutions" showEditIcon={true} />
                                                        </h3>
                                                        <div className="space-y-2">
                                                            {tabData.items?.filter(item => item.category === 'merchant').map((item) => (
                                                                <NavigationMenuLink
                                                                    key={item.id}
                                                                    href={item.url}
                                                                    className="block px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus:outline-none rounded-md"
                                                                >
                                                                    {item.key ? <EditableContent id={item.key} showEditIcon={true} /> : item.title}
                                                                </NavigationMenuLink>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Issuer Solutions Column */}
                                                    <div>
                                                        <h3 className="font-semibold text-lg mb-4 text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                                                            <EditableContent id="nav.issuer_solutions" showEditIcon={true} />
                                                        </h3>
                                                        <div className="space-y-2">
                                                            {tabData.items?.filter(item => item.category === 'issuer').map((item) => (
                                                                <NavigationMenuLink
                                                                    key={item.id}
                                                                    href={item.url}
                                                                    className="block px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus:outline-none rounded-md"
                                                                >
                                                                    {item.key ? <EditableContent id={item.key} showEditIcon={true} /> : item.title}
                                                                </NavigationMenuLink>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Get Started Column */}
                                                    <div>
                                                        <h3 className="font-semibold text-lg mb-4 text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                                                            <EditableContent id="nav.get_started" showEditIcon={true} />
                                                        </h3>
                                                        <div className="space-y-4">
                                                            {/* Image placeholder */}
                                                            <div className="w-full h-32 bg-gradient-to-br from-[#ED2245]/10 to-[#ED2245]/20 rounded-lg flex items-center justify-center border border-[#ED2245]/20">
                                                                <div className="text-center">
                                                                    <div className="w-24 h-24 mx-auto mb-2 bg-[#ED2245]/20 rounded-full flex items-center justify-center">
                                                                        <Image src="/images/rivet-icons_question-mark-solid.svg" alt="Question Mark" width={82} height={82} />
                                                                    </div>

                                                                </div>
                                                            </div>

                                                            {/* CTA Links */}
                                                            <div className="space-y-2">
                                                                {tabData.items?.filter(item => item.category === 'cta').map((item) => (
                                                                    <NavigationMenuLink
                                                                        key={item.id}
                                                                        href={item.url}
                                                                        className="block px-3 py-2 text-sm text-slate-700 hover:bg-[#ED2245]/10 hover:text-[#ED2245] transition-colors focus:outline-none rounded-md border border-transparent hover:border-[#ED2245]/20"
                                                                    >
                                                                        {item.key ? <EditableContent id={item.key} showEditIcon={true} /> : item.title}
                                                                    </NavigationMenuLink>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Regular dropdown layout for other items */
                                            <div className="w-64 p-2 bg-white dark:bg-black shadow-lg border border-slate-200 dark:border-slate-700 rounded-lg">
                                                {tabData.items?.map((item) => (
                                                    <NavigationMenuLink
                                                        key={item.id}
                                                        href={item.url}
                                                        className="block px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus:outline-none rounded-md"
                                                    >
                                                        {item.key ? <EditableContent id={item.key} showEditIcon={true} /> : item.title}
                                                    </NavigationMenuLink>
                                                ))}
                                            </div>
                                        )}
                                    </NavigationMenuContent>
                                </NavigationMenuItem>
                            );
                        }

                        // For regular items or onClick-only items
                        return (
                            <NavigationMenuItem key={idx}>
                                {tabData.url ? (
                                    <Link
                                        href={tabData.url}
                                        prefetch={false}
                                        onClick={() => {
                                            setSelected(tabs.indexOf(tabData));
                                            if ('onClick' in tabData && tabData.onClick) tabData.onClick();
                                        }}
                                        className={`relative z-10 flex items-center rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${isSelected
                                            ? "text-[#ED2245]"
                                            : "text-slate-700 dark:text-white hover:text-[#ED2245] dark:hover:text-[#ED2245]"
                                            }`}
                                        aria-label={tabData.title}
                                    >
                                        {isSelected && (
                                            <motion.div
                                                layoutId="pill"
                                                className="absolute inset-0 z-0 rounded-xl bg-[#ED2245]/10 border border-[#ED2245]/30 shadow-sm"
                                                transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                            />
                                        )}
                                        <span className="relative z-10 flex items-center gap-2">
                                            <Icon className="h-5 w-5 flex-shrink-0" />
                                            <AnimatePresence initial={false}>
                                                {isSelected && (
                                                    <motion.span variants={spanVariants} initial="initial" animate="animate" exit="exit" className="overflow-hidden whitespace-nowrap">
                                                        {tabData.key ? <EditableContent id={tabData.key} showEditIcon={true} /> : tabData.title}
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </span>
                                    </Link>
                                ) : (
                                    <button
                                        onClick={() => {
                                            // Language is an action, not a page: keep the pill on the active tab
                                            if (!isLanguageTab) setSelected(tabs.indexOf(tabData));
                                            if ('onClick' in tabData && tabData.onClick) tabData.onClick();
                                        }}
                                        className={`relative z-10 flex items-center rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${isSelected
                                            ? "text-[#ED2245]"
                                            : "text-slate-700 dark:text-white hover:text-[#ED2245] dark:hover:text-[#ED2245]"
                                            }`}
                                        aria-label={tabData.title}
                                    >
                                        {isSelected && (
                                            <motion.div
                                                layoutId="pill"
                                                className="absolute inset-0 z-0 rounded-xl bg-[#ED2245]/10 border border-[#ED2245]/30 shadow-sm"
                                                transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                            />
                                        )}
                                        <span className="relative z-10 flex items-center gap-2">
                                            <Icon className={`${isLanguageTab ? 'h-6 w-6' : 'h-5 w-5'} flex-shrink-0`} />
                                            {isLanguageTab && <LanguageNativeName className="hidden lg:inline" />}
                                            {!isLanguageTab && (
                                                <AnimatePresence initial={false}>
                                                    {isSelected && (
                                                        <motion.span variants={spanVariants} initial="initial" animate="animate" exit="exit" className="overflow-hidden whitespace-nowrap">
                                                            {tabData.key ? <EditableContent id={tabData.key} showEditIcon={true} /> : tabData.title}
                                                        </motion.span>
                                                    )}
                                                </AnimatePresence>
                                            )}
                                        </span>
                                    </button>
                                )}
                            </NavigationMenuItem>
                        );
                    })}
                </NavigationMenuList>
            </NavigationMenu>
        </div>
    );
}
